import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertTriangle, X } from 'lucide-react';

/**
 * Chiede conferma ogni volta che si lascia un editor relazione, anche senza modifiche pendenti.
 * Copre refresh/chiusura scheda (beforeunload nativo), tasto indietro del browser (sentinel
 * + popstate), pulsante indietro in-app (requestBack) e i link interni come la sidebar
 * (intercettati in capture).
 * isDirty/lastSaved servono solo al testo del modale. suppressNextUnload() disattiva la
 * guardia una volta per le ricariche volute (es. reset bozza), altrimenti l'autosave
 * riscriverebbe la bozza appena cancellata.
 */
export function useUnsavedGuard(opts: {
    isDirty: boolean;
    lastSaved: Date | null;
    /** Restituisce false se il salvataggio non è riuscito: in quel caso si resta sulla pagina. */
    onSaveNow: () => Promise<boolean | void> | boolean | void;
    /** Dove andare col tasto indietro se la history non ha una pagina precedente. */
    fallbackPath?: string;
    /**
     * Destinazione del pulsante «Indietro» dell'editor (di solito il mandato). La history non
     * basta: scegliere editor e soggetto aggiunge voci, e go(-2) può tornare all'editor stesso.
     */
    backPath?: string;
}) {
    const { isDirty, lastSaved, onSaveNow, fallbackPath = '/pratiche', backPath } = opts;
    const navigate = useNavigate();

    const dirtyRef = useRef(isDirty);
    useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);

    const [promptOpen, setPromptOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // Cosa fare quando l'utente conferma l'uscita.
    const pending = useRef<{ kind: 'back'; inApp?: boolean } | { kind: 'link'; href: string } | null>(null);
    const confirmedLeave = useRef(false);
    // Salta la guardia una volta, per le ricariche programmatiche.
    const suppressUnload = useRef(false);

    const suppressNextUnload = useCallback(() => { suppressUnload.current = true; }, []);

    // Refresh, chiusura scheda, barra indirizzi.
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (suppressUnload.current) return;
            e.preventDefault();
            e.returnValue = ''; // richiesto da Chrome per mostrare il dialog
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // popstate: tasto indietro del browser. Sentinel per restare sulla pagina.
    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        const onPop = () => {
            if (confirmedLeave.current || suppressUnload.current) return;
            window.history.pushState(null, '', window.location.href); // resta qui
            pending.current = { kind: 'back' };
            setPromptOpen(true);
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // Link interni: il click viene intercettato in capture prima di React Router.
    // Solo link same-origin con click semplice (niente modificatori, _blank o download)
    // verso un URL diverso; link esterni e nuove schede passano.
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
            if (!a) return;
            if (a.target && a.target !== '_self') return;
            if (a.hasAttribute('download')) return;
            const href = a.getAttribute('href') || '';
            if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
            let url: URL;
            try { url = new URL(a.href, window.location.href); } catch { return; }
            if (url.origin !== window.location.origin) return;
            const dest = url.pathname + url.search + url.hash;
            const here = window.location.pathname + window.location.search + window.location.hash;
            if (dest === here) return;
            e.preventDefault();
            e.stopPropagation();
            pending.current = { kind: 'link', href: dest };
            setPromptOpen(true);
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, []);

    const doLeave = useCallback(() => {
        const p = pending.current;
        pending.current = null;
        confirmedLeave.current = true;
        if (p?.kind === 'link') {
            // Toglie il sentinel (stesso URL) e va alla destinazione.
            navigate(p.href);
            return;
        }
        if (p?.kind === 'back' && p.inApp && backPath) { navigate(backPath); return; }
        // Dal sentinel la pagina precedente reale è a -2. Se l'editor è stato aperto
        // da URL diretto non c'è: si usa il fallback.
        if (window.history.length < 3) { navigate(fallbackPath, { replace: true }); return; }
        window.history.go(-2);
    }, [navigate, fallbackPath, backPath]);

    // Usata dal pulsante indietro in-app al posto di navigate(-1).
    const requestBack = useCallback(() => {
        pending.current = { kind: 'back', inApp: true };
        setPromptOpen(true);
    }, []);

    const saveAndExit = useCallback(async () => {
        setSaving(true);
        let ok: boolean | void = true;
        try { ok = await onSaveNow(); } catch { ok = false; }
        setSaving(false);
        // Salvataggio fallito: si resta qui per non perdere il lavoro. Il modale resta
        // aperto e l'utente può riprovare o uscire senza salvare.
        if (ok === false) return;
        setPromptOpen(false);
        doLeave();
    }, [onSaveNow, doLeave]);

    const exitWithout = useCallback(() => { setPromptOpen(false); doLeave(); }, [doLeave]);

    const dismiss = useCallback(() => {
        setPromptOpen(false);
        pending.current = null;
    }, []);

    const modal = promptOpen ? (
        <UnsavedGuardModal lastSaved={lastSaved} isDirty={isDirty} saving={saving}
            onSave={saveAndExit} onDiscard={exitWithout} onCancel={dismiss} />
    ) : null;

    return { requestBack, modal, promptOpen, suppressNextUnload };
}

const UnsavedGuardModal: React.FC<{
    lastSaved: Date | null; isDirty: boolean; saving: boolean;
    onSave: () => void; onDiscard: () => void; onCancel: () => void;
}> = ({ lastSaved, isDirty, saving, onSave, onDiscard, onCancel }) => (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !saving && onCancel()}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <AlertTriangle size={20} className="text-amber-500"/>
                    <div>
                        <h3 className="text-lg font-black text-slate-900">Stai uscendo dalla relazione</h3>
                        <p className="text-xs text-slate-500 mt-1">
                            {lastSaved
                                ? `Ultimo salvataggio: ore ${lastSaved.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}.`
                                : 'Nessun salvataggio in questa sessione.'}
                            {' '}
                            {isDirty ? 'Ci sono modifiche non salvate. Vuoi salvare adesso prima di uscire?' : 'Vuoi salvare adesso prima di uscire?'}
                        </p>
                    </div>
                </div>
                <button onClick={onCancel} disabled={saving} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50"><X size={18}/></button>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-wrap justify-end gap-2">
                <button onClick={onCancel} disabled={saving}
                        className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-50">Annulla</button>
                <button onClick={onDiscard} disabled={saving}
                        className="px-4 py-2 text-sm font-bold text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50">Esci senza salvare</button>
                <button onClick={onSave} disabled={saving}
                        className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
                    <Save size={14}/> {saving ? 'Salvataggio…' : 'Salva ed esci'}
                </button>
            </div>
        </div>
    </div>
);
